import React, { useMemo } from 'react';
import ErrorAwareRoute from '@Generic/SaplingRoute/ErrorAwareRoute';
import { arrayOf, bool, elementType, oneOfType, string } from 'prop-types';
import CompanyTrialHeader from './CompanyTrial/CompanyTrialHeader';
import { useAppConfig, useLoggedInUser } from '@ApplicationWide/AppConfig';
import useRedirectBasedOnUserStatus from '../useRedirectBasedOnUserStatus';
import routePropType from '@ApplicationWide/routes/routePropType';
import { hasPermissionForRoute, hasRequiredRoleForRoute } from '@ApplicationWide/permissions/routes';
import NotAllowedPage from '@ApplicationWide/ErrorPages/NotAllowedPage/NotAllowedPage';
import { isCompanyTrialFeatureFlag, getDatesDifferenceInDays } from '@Utils/isCompanyTrialOrDateDifference';
import RedirectToLoginScreen from './RedirectToLoginScreen';
import SaplingIntercomProvider from './SaplingIntercomProvider';
import IdleDetection from './SessionOut/IdleDetection';
import './PrivateRoute.less';

// This component contains most of the main application frame and is not needed until after login.
// However, it will certainly be needed after login, so we tell webpack to "prefetch" this
// during idle time, so it will begin loading while the user is busy logging in.
//
// see https://webpack.js.org/api/module-methods/#magic-comments for more
const Navigation = React.lazy(() => import(/* webpackPrefetch: true */ './Navigation'));

const getPermissionsForRoute = (route, user, company) =>
  !route || (hasRequiredRoleForRoute(route, user) && hasPermissionForRoute(route, user, company));

const PrivateRoute = ({ exact, path, component: Component, hasNavBar, route = null, ...extraProps }) => {
  const { company } = useAppConfig();
  const { user } = useLoggedInUser();
  useRedirectBasedOnUserStatus(true);
  const trialDays = useMemo(() => getDatesDifferenceInDays(company), [company]);
  if (!user) return <RedirectToLoginScreen />;
  if (!getPermissionsForRoute(route, user, company)) return <NotAllowedPage />;

  return (
    <SaplingIntercomProvider>
      <IdleDetection>
        <ErrorAwareRoute
          exact={exact}
          path={path}
          render={(props) => {
            return hasNavBar ? (
              <>
                {isCompanyTrialFeatureFlag(company, trialDays) && <CompanyTrialHeader trialDays={trialDays} />}
                <div className='navigation-page'>
                  <Navigation />
                  <Component {...props} {...extraProps} />
                </div>
              </>
            ) : (
              <Component {...props} {...extraProps} />
            );
          }}
        />
      </IdleDetection>
    </SaplingIntercomProvider>
  );
};

PrivateRoute.propTypes = {
  exact: bool,
  path: oneOfType([string, arrayOf(string)]),
  component: elementType,
  requiredRoles: arrayOf(string),
  hasNavBar: bool,
  route: routePropType,
};

export default PrivateRoute;
